// src/pages/NetworkAlerts/AlertFormModal.js — criar/editar alerta de falha por área
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";

import useStyles from "./styles";
import {
  SCOPE_TYPES,
  SCOPE_ALL,
  SCOPE_ALL_VALUE,
  scopeTypeLabel,
  scopePlaceholder,
  buildAlertText,
  toDatetimeLocal,
  fromDatetimeLocal,
} from "./constants";

const TITLE_MAX = 80;

const buildInitial = (alert, prefill, erps) => {
  const src = alert || prefill || {};
  const erpId =
    src.erpIntegrationId ?? src.erpIntegration?.id ?? (erps.length === 1 ? erps[0].id : "");
  const scopeType = src.scopeType || "pon";
  return {
    erpIntegrationId: erpId === "" ? "" : String(erpId),
    scopeType,
    scopeValue: scopeType === SCOPE_ALL ? "" : src.scopeValue || "",
    title: src.title || "",
    message: src.message || "",
    startsAt: toDatetimeLocal(src.startsAt || new Date()),
    endsAt: toDatetimeLocal(src.endsAt),
    active: alert ? !!alert.active : true,
    affectedCount: src.affectedCount,
  };
};

/**
 * Props: open, alert? (edição), prefill? ({erpIntegrationId, scopeType, scopeValue,
 * affectedCount}), erps, onClose, onSaved(alertSalvo).
 */
const AlertFormModal = ({ open, alert, prefill, erps, onClose, onSaved }) => {
  const classes = useStyles();
  const { datetimeToClient } = useDate();

  const [form, setForm] = useState(() => buildInitial(alert, prefill, erps));

  // Foto do aviso: arquivo escolhido agora (sobe depois de salvar) ou já gravada (alert.mediaUrl)

  const [photoFile, setPhotoFile] = useState(null);

  const [photoPreview, setPhotoPreview] = useState(alert && alert.mediaUrl ? alert.mediaUrl : "");

  const [removePhoto, setRemovePhoto] = useState(false);

  const onPickPhoto = (e) => {

    const f = e.target.files && e.target.files[0];

    if (!f) return;

    if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) { toast.error(i18n.t("networkAlerts.form.photoType")); return; }

    if (f.size > 5 * 1024 * 1024) { toast.error(i18n.t("networkAlerts.form.photoSize")); return; }

    setPhotoFile(f); setRemovePhoto(false);

    setPhotoPreview(URL.createObjectURL(f));

  };

  const clearPhoto = () => { setPhotoFile(null); setPhotoPreview(""); setRemovePhoto(true); };

  const syncPhoto = async (id) => {

    if (photoFile) {

      const fd = new FormData();

      fd.append("typeArch", "alerts"); // antes do arquivo: define a pasta public/company<id>/alerts

      fd.append("file", photoFile);

      const { data } = await api.post(`/network-alerts/${id}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });

      return data;

    }

    if (removePhoto && alert && alert.mediaUrl) {

      const { data } = await api.delete(`/network-alerts/${id}/media`);

      return data;

    }

    return null;

  };
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(buildInitial(alert, prefill, erps));
  }, [open, alert, prefill, erps]);

  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isAll = form.scopeType === SCOPE_ALL;

  const validate = () => {
    const errors = [];
    if (!form.erpIntegrationId) errors.push(i18n.t("networkAlerts.validation.erpRequired"));
    if (!isAll && !form.scopeValue.trim()) {
      errors.push(i18n.t("networkAlerts.validation.scopeValueRequired"));
    }
    if (!form.title.trim()) errors.push(i18n.t("networkAlerts.validation.titleRequired"));
    if (!form.message.trim()) errors.push(i18n.t("networkAlerts.validation.messageRequired"));
    const starts = fromDatetimeLocal(form.startsAt);
    const ends = fromDatetimeLocal(form.endsAt);
    if (starts && ends && new Date(ends) < new Date(starts)) {
      errors.push(i18n.t("networkAlerts.validation.endsBeforeStarts"));
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const errors = validate();
    if (errors.length) {
      errors.forEach((msg) => toast.error(msg));
      return;
    }
    const body = {
      erpIntegrationId: Number(form.erpIntegrationId),
      scopeType: form.scopeType,
      scopeValue: isAll ? SCOPE_ALL_VALUE : form.scopeValue.trim(),
      title: form.title.trim(),
      message: form.message.trim(),
      startsAt: fromDatetimeLocal(form.startsAt) || undefined,
      endsAt: fromDatetimeLocal(form.endsAt),
      active: form.active,
    };
    if (form.affectedCount != null && !alert) body.affectedCount = Number(form.affectedCount);

    setSaving(true);
    try {
      const { data } = alert
        ? await api.put(`/network-alerts/${alert.id}`, body)
        : await api.post("/network-alerts", body);
      let saved = data;
      try {
        const withPhoto = await syncPhoto((data && data.id) || (alert && alert.id));
        if (withPhoto) saved = withPhoto;
      } catch (photoErr) {
        toastError(photoErr);
      }
      toast.success(
        i18n.t(alert ? "networkAlerts.toasts.updated" : "networkAlerts.toasts.created")
      );
      onSaved(saved && saved.id ? saved : { ...(alert || {}), ...body, ...(saved || {}) });
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const previewText = buildAlertText(
    { title: form.title, message: form.message, endsAt: fromDatetimeLocal(form.endsAt) },
    datetimeToClient
  );

  const selectedErp = erps.find((it) => String(it.id) === form.erpIntegrationId);
  const requiredMark = (
    <span className={classes.required} aria-hidden="true">
      *
    </span>
  );

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      fullWidth
      maxWidth="md"
      scroll="paper"
      aria-labelledby="network-alert-form-title"
    >
      <form className={classes.dialogForm} onSubmit={handleSubmit} noValidate>
        <DialogTitle id="network-alert-form-title">
          {i18n.t(alert ? "networkAlerts.form.titleEdit" : "networkAlerts.form.titleNew")}
          <div className={classes.dialogSubtitle}>{i18n.t("networkAlerts.form.subtitle")}</div>
        </DialogTitle>
        <DialogContent>
          <div className={classes.formGrid}>
            <div className={`${classes.sectionTitle} ${classes.sectionTitleFirst}`}>Área afetada</div>

            <div className={`${classes.field} ${classes.full}`}>
              <label className={classes.label} id="network-alert-erp-label">
                {i18n.t("networkAlerts.form.erp")}
                {requiredMark}
              </label>
              <TextField
                select
                id="network-alert-erp"
                value={form.erpIntegrationId}
                onChange={set("erpIntegrationId")}
                variant="outlined"
                size="small"
                fullWidth
                required
                disabled={!!alert}
                SelectProps={{
                  labelId: "network-alert-erp-label",
                  displayEmpty: true,
                  renderValue: () =>
                    selectedErp ? (
                      `${selectedErp.name} (${selectedErp.type})`
                    ) : (
                      <span className={classes.placeholder}>Selecione…</span>
                    ),
                }}
              >
                {erps.map((it) => (
                  <MenuItem key={it.id} value={String(it.id)}>
                    {it.name} ({it.type})
                  </MenuItem>
                ))}
              </TextField>
            </div>

            <div className={classes.field}>
              <label className={classes.label} id="network-alert-scope-type-label">
                {i18n.t("networkAlerts.form.scopeType")}
              </label>
              <TextField
                select
                id="network-alert-scope-type"
                value={form.scopeType}
                onChange={set("scopeType")}
                variant="outlined"
                size="small"
                fullWidth
                SelectProps={{ labelId: "network-alert-scope-type-label" }}
              >
                {SCOPE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {scopeTypeLabel(type)}
                  </MenuItem>
                ))}
                <MenuItem value={SCOPE_ALL}>{i18n.t("networkAlerts.scope.all")}</MenuItem>
              </TextField>
            </div>

            <div className={classes.field}>
              <label className={classes.label} htmlFor="network-alert-scope-value">
                {i18n.t("networkAlerts.form.scopeValue")}
                {!isAll && requiredMark}
              </label>
              <TextField
                id="network-alert-scope-value"
                placeholder={isAll ? "" : scopePlaceholder(form.scopeType)}
                value={isAll ? "" : form.scopeValue}
                onChange={set("scopeValue")}
                variant="outlined"
                size="small"
                fullWidth
                required={!isAll}
                disabled={isAll}
              />
              {isAll && (
                <span className={classes.help}>{i18n.t("networkAlerts.form.scopeAllHelp")}</span>
              )}
            </div>

            <div className={classes.sectionTitle}>Mensagem</div>

            <div className={`${classes.field} ${classes.full}`}>
              <label className={classes.label} htmlFor="network-alert-title">
                {i18n.t("networkAlerts.form.title")}
                {requiredMark}
              </label>
              <TextField
                id="network-alert-title"
                placeholder={i18n.t("networkAlerts.form.titlePlaceholder")}
                value={form.title}
                onChange={set("title")}
                variant="outlined"
                size="small"
                fullWidth
                required
                inputProps={{ maxLength: TITLE_MAX }}
              />
              <span className={classes.help}>{`${form.title.length}/${TITLE_MAX}`}</span>
            </div>

            <div className={`${classes.field} ${classes.full}`}>
              <label className={classes.label} htmlFor="network-alert-message">
                {i18n.t("networkAlerts.form.message")}
                {requiredMark}
              </label>
              <TextField
                id="network-alert-message"
                placeholder={i18n.t("networkAlerts.form.messagePlaceholder")}
                value={form.message}
                onChange={set("message")}
                variant="outlined"
                size="small"
                fullWidth
                required
                multiline
                minRows={3}
                maxRows={6}
              />
              <span className={classes.help}>{i18n.t("networkAlerts.form.messageHelp")}</span>
            </div>

            <div className={classes.field}>
              <span className={classes.label}>{i18n.t("networkAlerts.form.preview")}</span>
              <div className={classes.previewBox} aria-live="polite">
                {previewText || (
                  <span className={classes.previewEmpty}>
                    {i18n.t("networkAlerts.preview.placeholder")}
                  </span>
                )}
              </div>
            </div>

            <div className={classes.field}>
              <span className={classes.label}>Foto</span>
              <div className={classes.photoRow}>
                {photoPreview ? (
                  <img src={photoPreview} alt="" className={classes.photoThumb} />
                ) : null}
                <Button variant="outlined" size="small" component="label" disabled={saving}>
                  {photoPreview ? i18n.t("networkAlerts.form.photoChange") : i18n.t("networkAlerts.form.photoAttach")}
                  <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onPickPhoto} />
                </Button>
                {photoPreview ? (
                  <Button size="small" className={classes.dangerText} onClick={clearPhoto} disabled={saving}>
                    {i18n.t("networkAlerts.form.photoRemove")}
                  </Button>
                ) : null}
              </div>
              <span className={classes.help}>{i18n.t("networkAlerts.form.photoHelp")}</span>
            </div>

            <div className={classes.sectionTitle}>Vigência</div>

            <div className={classes.field}>
              <label className={classes.label} htmlFor="network-alert-starts-at">
                {i18n.t("networkAlerts.form.startsAt")}
              </label>
              <TextField
                id="network-alert-starts-at"
                type="datetime-local"
                value={form.startsAt}
                onChange={set("startsAt")}
                variant="outlined"
                size="small"
                fullWidth
              />
            </div>

            <div className={classes.field}>
              <label className={classes.label} htmlFor="network-alert-ends-at">
                {i18n.t("networkAlerts.form.endsAt")}
              </label>
              <TextField
                id="network-alert-ends-at"
                type="datetime-local"
                value={form.endsAt}
                onChange={set("endsAt")}
                variant="outlined"
                size="small"
                fullWidth
              />
              <span className={classes.help}>{i18n.t("networkAlerts.form.endsAtHelp")}</span>
            </div>

            <div className={classes.full}>
              <FormControlLabel
                control={
                  <Switch color="primary" checked={form.active} onChange={set("active")} />
                }
                label={i18n.t("networkAlerts.form.active")}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            {i18n.t("networkAlerts.buttons.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.btnWrapper}
            disabled={saving}
          >
            {i18n.t(
              prefill?.broadcastAfter
                ? "networkAlerts.buttons.saveAndBroadcast"
                : "networkAlerts.buttons.save"
            )}
            {saving && <CircularProgress size={20} className={classes.buttonProgress} />}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AlertFormModal;
